import queryString from 'query-string'
import { useCallback, useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useLocation } from 'react-router-dom'

import { useAuth } from 'components/AuthProvider'
import { loginPath, resendConfirmationEmailPath } from 'components/Routes'

import {
  VisibilityOffRounded as VisibilityOffIcon,
  VisibilityRounded as VisibilityIcon
} from '@mui/icons-material'
import {
  Grid2,
  IconButton,
  InputAdornment,
  Link,
  Typography
} from '@mui/material'
import Button from 'components/Button'
import DefaultLayout from 'components/layout/DefaultLayout'
import { Link as RouterLink } from 'react-router-dom'

import { Theme } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import PasswordProgress from 'components/PasswordProgress'
import { Field, FieldProps, Formik } from 'formik'
import { TextField } from 'formik-mui'
import * as Yup from 'yup'

const useStyles = makeStyles()((theme: Theme) => ({
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(2)
  },
  link: {
    color: theme.palette.primary.main
  }
}))

interface FormValues {
  email: string
  confirmationCode: string
  newPassword: string
  confirmPassword: string
}

const validationSchema = Yup.object().shape({
  email: Yup.string().email('Email is invalid').required('Email is required'),
  confirmationCode: Yup.string()
    .required('Verification code is required')
    .matches(/^\d{6}$/, 'Enter the 6-digit code from your email'),
  confirmPassword: Yup.string()
    .required('Confirm new password is required')
    .oneOf([Yup.ref('newPassword'), null], `Confirm new password doesn't match`)
})

export default function ResetPassword(): JSX.Element {
  const { classes } = useStyles()
  const location = useLocation()

  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [disableButton, setDisableButton] = useState<boolean>(false)

  const handleToggleShowPassword = () => setShowPassword(!showPassword)

  const { reset, loading } = useAuth()

  // forgot-password may forward the email in the query string; the 6-digit code
  // is emailed by the backend and entered here (there is no reset link token).
  const { email: emailFromQuery } = queryString.parse(location.search)

  const initialValues: FormValues = {
    email: (emailFromQuery as string) ?? '',
    confirmationCode: '',
    newPassword: '',
    confirmPassword: ''
  }

  const recaptchaEnabled = JSON.parse(
    process.env.REACT_APP_RECAPTCHA_ENABLED || 'true'
  )
  const { executeRecaptcha } = useGoogleReCaptcha()

  async function handleSubmit(values: FormValues) {
    if (!executeRecaptcha && recaptchaEnabled) return

    const recaptchaToken =
      recaptchaEnabled &&
      executeRecaptcha &&
      (await executeRecaptcha('passwords/reset'))

    reset(
      values.email,
      values.confirmationCode,
      values.newPassword,
      recaptchaToken || undefined
    )
  }

  const renderHeader = useCallback(() => {
    return (
      <Typography component='h1' variant='h1' fontSize='4.5rem' color='primary'>
        Change password
      </Typography>
    )
  }, [])

  return (
    <DefaultLayout
      backgroundColor='white'
      containerMaxWidth='xs'
      heading={renderHeader()}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ setFieldValue, handleSubmit }) => (
          <form className={classes.form} onSubmit={handleSubmit}>
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12 }}>
                <Field name='email'>
                  {({ field, form, meta }: FieldProps) => (
                    <TextField
                      field={field}
                      form={form}
                      meta={meta}
                      fullWidth
                      required
                      label='Email address'
                      onChange={e => {
                        setFieldValue('email', e.target.value)
                      }}
                    />
                  )}
                </Field>
              </Grid2>

              <Grid2 size={{ xs: 12 }}>
                <Field name='confirmationCode'>
                  {({ field, form, meta }: FieldProps) => (
                    <TextField
                      field={field}
                      form={form}
                      meta={meta}
                      fullWidth
                      required
                      label='Verification code'
                      inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                      onChange={e => {
                        setFieldValue('confirmationCode', e.target.value)
                      }}
                    />
                  )}
                </Field>
              </Grid2>

              <Grid2 size={{ xs: 12 }}>
                <PasswordProgress
                  autoComplete='new-password'
                  label='New password'
                  required
                  onChange={(password: string, hasError?: boolean) => {
                    setFieldValue('newPassword', password)
                    if (hasError) setDisableButton(true)
                    else setDisableButton(false)
                  }}
                />
              </Grid2>

              <Grid2 size={{ xs: 12 }}>
                <Field name='confirmPassword' autoComplete='new-password'>
                  {({ field, form, meta }: FieldProps) => (
                    <TextField
                      field={field}
                      form={form}
                      meta={meta}
                      disabled={false}
                      fullWidth
                      label='Confirm new password'
                      required
                      onChange={e => {
                        setFieldValue('confirmPassword', e.target.value)
                      }}
                      InputProps={{
                        style: { background: '#ffffff' },
                        // This is where the toggle 'view password' button is added
                        endAdornment: (
                          <InputAdornment position='end'>
                            <IconButton
                              aria-label='toggle password visibility'
                              onClick={() => handleToggleShowPassword()}
                              size='large'
                              tabIndex={-1}
                            >
                              {showPassword ? (
                                <VisibilityIcon />
                              ) : (
                                <VisibilityOffIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      sx={{ background: 'none' }}
                      type={showPassword ? 'text' : 'password'}
                    />
                  )}
                </Field>
              </Grid2>

              <Grid2 mt={2} size={{ xs: 12 }}>
                <Button
                  color='primary'
                  fullWidth
                  disabled={loading || disableButton}
                  type='submit'
                >
                  Change password
                </Button>
              </Grid2>
              <Grid2 container>
                <Link
                  color='textPrimary'
                  component={RouterLink}
                  to={loginPath()}
                  underline='always'
                  className={classes.link}
                >
                  Go back to login
                </Link>
              </Grid2>
              <Grid2 container>
                <Typography align='center' variant='body1'>
                  <Link
                    color='textPrimary'
                    component={RouterLink}
                    to={resendConfirmationEmailPath()}
                    underline='always'
                    className={classes.link}
                  >
                    Resend confirmation email
                  </Link>
                </Typography>
              </Grid2>
            </Grid2>
          </form>
        )}
      </Formik>
    </DefaultLayout>
  )
}
